import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import {
	AppGlobalSearchResultItem,
	AppSearchRequestModel,
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	searchApps,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	EMPTY_ARRAY,
	ensureNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	useForceRerender,
	useUserTimezone
} from "client/Hooks";
import {
	NoResults,
	SelectOption,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	getPropertySortText
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ScopeLayout } from "../ScopeLayout";
import { SearchSettings } from "../SearchSettings";
import { AppCategory } from "client/Api/Apps/common";
import { SortDirection } from "client/Api/common";
import { categories, hiddenCategoryIds } from "../../Apps/Store/CategoryFilter";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { SearchNoResults } from "../SearchNoResults";
import { TimeZone } from "client/DateAndTime/timezones";
import { checkHasFilter } from "./common";
import { AppItemView } from "../Items/AppItemView";

export type AppSearchProps = {
	searchTerm: VersionedValue<string>;
	mobileSearchSettingsTarget: HTMLDivElement | null;
};

export const AppSearch = (props: AppSearchProps): ReactElement => {
	const {
		searchTerm,
		mobileSearchSettingsTarget
	} = props;

	const [currentItems, setCurrentItems] = useState<readonly AppGlobalSearchResultItem[]>([]);
	const [loadingState, setLoadingState] = useState<LoadingState>("loading");
	const [formData, setFormData] = useState<AppSearchFormData>({
		sortDirection: SortDirection.Ascending,
		...defaultFilters
	});

	const [reload, appSearchVersion] = useForceRerender();

	const userTimezone: TimeZone = useUserTimezone();

	const formHandler: IFormHandler<AppSearchFormData> = useFormHandler(
		formData,
		setFormData,
		"NoValidation"
	);

	const {
		sortDirection: _,
		...filters
	} = formData;

	const hasFilter: boolean = checkHasFilter<FilterModel>(filters, defaultFilters);
	const listHandle: RefObject<ScrollableVirtualListHandle<AppGlobalSearchResultItem> | null> = useRef<ScrollableVirtualListHandle<AppGlobalSearchResultItem>>(null);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(formData.sortDirection);
			hasImplicitDependencyOn(formData.categories);
			hasImplicitDependencyOn(searchTerm);

			listHandle.current
				?.scrollToTop();
		},
		[
			formData.categories,
			formData.sortDirection,
			searchTerm
		]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(appSearchVersion);

			if (!searchTerm.value) {
				setCurrentItems([]);
				setLoadingState("ready");
				return;
			}

			async function fetchApps(signal: AbortSignal): Promise<void> {
				try {
					setLoadingState("loading");

					const items: readonly AppGlobalSearchResultItem[] = await searchApps(
						{
							categories: formData.categories,
							sortDirection: formData.sortDirection,
							searchTerm: searchTerm.value
						},
						signal
					);

					setCurrentItems(items);
					setLoadingState("ready");
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setLoadingState("error");
				}
			}

			const cancellation = new AbortController();
			fetchApps(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			appSearchVersion,
			formData.categories,
			formData.sortDirection,
			searchTerm
		]
	);

	const clearHandler = useCallback(
		(): void => {
			setFormData(formData => ({
				...defaultFilters,
				sortDirection: formData.sortDirection
			}));

			formHandler.validation
				.triggerInputFinished();
		},
		[formHandler.validation]
	);

	const renderContent = (): ReactElement => {
		if (loadingState === "ready" && currentItems.length === 0) {
			return <SearchNoResults />;
		}

		if (loadingState === "loading" || currentItems.length > 0) {
			return (
				<ScrollableVirtualList
					items={currentItems}
					estimatedHeightOfAnItem={70}
					itemKey={getUniqueKey}
					ref={listHandle}
					trackingId="app-search-results"
					renderAfterItems={
						<>
							<ScrollableVirtualList.LoadMoreIndicator
								isLoadingData={loadingState === "loading"}
								hasMoreItems={false}
								onRequestMoreItems={() => {/** NOOP */}}
							/>
							<ScrollableVirtualList.EndOfListSpacing />
						</>
					}
				>
					{(item: AppGlobalSearchResultItem, index: number): ReactElement => (
						<AppItemView
							key={item.id}
							navigationIndex={index}
							item={item}
							itemScope={appScope}
							timezone={userTimezone}
						/>
					)}
				</ScrollableVirtualList>
			);
		}

		return (
			<NoResults
				trackingId="has-error"
				icon="gear"
				title={localize("_NotAbleToRetrieveSearchResults")}
				message={localize("_WeWereUnableToRetrieveSearchResults")}
				onRetry={reload}
				height="fill"
			/>
		);
	};

	return (
		<ScopeLayout>
			<ScopeLayout.Settings>
				<SearchSettings
					menuTarget={mobileSearchSettingsTarget}
					onClear={clearHandler}
					hasFilter={hasFilter}
				>
					<SearchSettings.Select
						label={localize("_SortBy")}
						attachable={formHandler.for("sortDirection")}
						options={sortOptions}
						isExpandedByDefault
					/>
					<SearchSettings.MultiSelect
						label={localize("_Category")}
						attachable={formHandler.for("categories")}
						options={categoryOptions}
						placeholder={localize("_SelectCategory(ies)")}
					/>
				</SearchSettings>
			</ScopeLayout.Settings>

			<ScopeLayout.Results>
				{renderContent()}
			</ScopeLayout.Results>
		</ScopeLayout>
	);
};
AppSearch.displayName = "AppSearch";

type LoadingState = "loading" | "error" | "ready";

type AppSearchFormData = Omit<AppSearchRequestModel, "searchTerm">;

type FilterModel = Omit<AppSearchFormData, "sortDirection">;

const defaultFilters: FilterModel = {
	categories: EMPTY_ARRAY
};

const appScope: GlobalSearchScopeIdentifier = {
	kind: GlobalSearchScopeKind.Predefined,
	predefinedKind: SearchIntegrationKind.Apps
};

function getUniqueKey(item: AppGlobalSearchResultItem): string {
	return `${item.id}`;
}

const sortOptions: readonly SelectOption<SortDirection>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: SortDirection.Ascending,
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: SortDirection.Descending,
		valueTrackingId: "name-z-a"
	}
];

const categoryOptions: SelectOption<AppCategory>[] = categories
	.filter(x => x.id !== null && !hiddenCategoryIds.includes(x.id))
	.map(
		(category): SelectOption<AppCategory> => ({
			text: category.name,
			value: ensureNotNil(category.id)
		})
	);
